import {
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  CalendarToday as CalendarTodayIcon,
  ChatOutlined as ChatOutlinedIcon,
  CloudOutlined,
  DeleteOutline as DeleteOutlinedIcon,
  Edit as EditIcon,
  GridOn,
  GridView as GridViewIcon,
  Image as ImageIcon,
  SaveAlt,
} from '@mui/icons-material';
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined';
import { Button, MenuItem, Typography } from '@mui/material';
import { isEmpty, toNumber, toString } from 'lodash';
import { MouseEvent, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PublicationModal } from 'src/components/modal';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { PATHS } from 'src/constants/paths';
import { useModal } from 'src/hooks';
import { PublicationTypes } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';

import { ActionBarProps } from '../actionBar/ActionBar.interface';
import {
  FastfoodIconStyled,
  GridViewIconStyled,
  MenuWrapper,
  PreviewButton,
  ScheduleComponentButton,
  StyledMenu,
  Wrapper,
} from './ActionBar.styled';

export const ActionBar = (props: ActionBarProps) => {
  const { isViewOnly, isOwnerOperator } = useUserRole();
  const { productCategoryId = '' } = useParams();
  const navigate = useNavigate();
  const publishModal = useModal();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorE2, setAnchorE2] = useState<null | HTMLElement>(null);

  const onToggleBulkReplaceMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl((state) => (!isEmpty(state) ? null : event.currentTarget));
  };
  const onToggleCreateMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorE2((state) => (!isEmpty(state) ? null : event.currentTarget));
  };
  const isLayoutMissing = props.hasLayouts === false;

  const navigateToPublicationList = () => {
    return (
      <Button
        variant="outlined"
        disabled={props.disabled}
        startIcon={<CloudOutlined />}
        onClick={() => navigate(PATHS.LIST_PUBLICATIONS)}>
        {lang.publications}
      </Button>
    );
  };

  const navigateToProductPublicationList = () => {
    return (
      <Button
        variant="outlined"
        onClick={() => navigate(PATHS.PRODUCT_PUBLICATIONS)}>
        {lang.productPublications}
      </Button>
    );
  };

  const returnToPublicationList = () => {
    return (
      <Button
        variant="outlined"
        disabled={props.disabled}
        onClick={() => navigate(PATHS.LIST_PUBLICATIONS)}>
        {lang.returnToPublications}
      </Button>
    );
  };
  const excludedTitlesSet = new Set([
    lang.assembly,
    lang.layout,
    lang.assemblyTags,
    lang.components,
    lang.products,
    lang.manageComponentTag,
    lang.productCategory,
    lang.locations,
  ]);

  const navigateToAssemblyPublication = () => {
    return (
      <Button
        variant="outlined"
        disabled={props.disabled}
        onClick={() => navigate(PATHS.ASSEMBLY_PUBLICATIONS)}>
        {lang.assemblyPublications}
      </Button>
    );
  };

  return (
    <>
      <Wrapper>
        {!props.title || !excludedTitlesSet.has(props.title) ? (
          props.numOfSelected ? (
            <Typography>{`${toNumber(toString(props.numOfSelected))}/${toNumber(
              toString(props.numResults),
            )} selected`}</Typography>
          ) : props.numResults !== undefined ? (
            props.numResults === 0 ? (
              <Typography>{lang.zeroResults}</Typography>
            ) : toNumber(props.maxResults) > 0 ? (
              <Typography>{`${toNumber(toString(props.numResults))}/${toNumber(
                toString(props.maxResults),
              )} results`}</Typography>
            ) : (
              <Typography>{`${toNumber(
                toString(props.numResults),
              )} results`}</Typography>
            )
          ) : null
        ) : null}

        {props.onExportClick && (
          <Button
            variant="outlined"
            disabled={!Boolean(props.numOfSelected)}
            startIcon={<SaveAlt />}
            onClick={props.onExportClick}>
            {lang.exportSelected}
          </Button>
        )}

        {(props.onBulkReplace || props.onEditCODMessaging) && !isViewOnly && (
          <>
            {!isViewOnly && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                endIcon={<ArrowDropDownIcon />}
                disabled={
                  !Boolean(props.numOfSelected) || props.disableBulkAction
                }
                onClick={onToggleBulkReplaceMenu}>
                {lang.bulkAction}
              </Button>
            )}

            <StyledMenu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={onToggleBulkReplaceMenu}>
              {props.onEditCODMessaging && (
                <MenuItem
                  onClick={(e) => {
                    onToggleBulkReplaceMenu(e);
                    props.onEditCODMessaging?.();
                  }}
                  disableRipple>
                  <MenuWrapper>
                    <ChatOutlinedIcon />
                    {lang.editCODMessaging}
                  </MenuWrapper>
                </MenuItem>
              )}
              {props.onBulkReplace && (
                <MenuItem onClick={onToggleBulkReplaceMenu} disableRipple>
                  <MenuWrapper onClick={props.onBulkReplace}>
                    <ImageIcon />
                    {lang.replaceComponents}
                  </MenuWrapper>
                </MenuItem>
              )}
            </StyledMenu>
          </>
        )}
        {props.onViewLayout && (
          <Button
            variant="outlined"
            startIcon={<GridOn />}
            onClick={props.onViewLayout}>
            {lang.viewLayouts}
          </Button>
        )}
        {props.type === lang.products && (
          <StyledMenu
            anchorEl={anchorE2}
            open={Boolean(anchorE2)}
            onClose={onToggleCreateMenu}
            boxPadding="6px">
            <MenuItem onClick={() => navigate(PATHS.PRODUCT_ADD)} disableRipple>
              <MenuWrapper>
                <FastfoodIconStyled />
                Product
              </MenuWrapper>
            </MenuItem>
            <MenuItem
              onClick={() => navigate(PATHS.PRODUCT_CATEGORIES_ADD)}
              disableRipple>
              <MenuWrapper>
                <GridViewIconStyled />
                {lang.productCategory}
              </MenuWrapper>
            </MenuItem>
          </StyledMenu>
        )}
        {props.type === lang.productCategory && (
          <>
            {isViewOnly ? (
              <ViewOnlyAlert />
            ) : (
              <Button
                variant="outlined"
                onClick={() =>
                  navigate(PATHS.PRODUCT_CATEGORIES_EDIT(productCategoryId))
                }
                startIcon={<EditIcon />}>
                {lang.edit}
              </Button>
            )}
          </>
        )}

        {props.type === lang.reviewProducts && (
          <>
            {isViewOnly ? (
              <ViewOnlyAlert />
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={props.onHandleClick}
                  startIcon={<EditIcon />}>
                  {lang.edit}
                </Button>
              </>
            )}
          </>
        )}

        {props.showPreview && (
          <>
            <PreviewButton
              disabled={props.previewDisabled}
              variant="outlined"
              startIcon={<PlayCircleOutlinedIcon />}
              onClick={props.onPreviewClick}>
              {lang.preview}
            </PreviewButton>
            {isViewOnly && <ViewOnlyAlert />}
          </>
        )}

        {props.showViewOnly && <>{isViewOnly && <ViewOnlyAlert />}</>}

        {props.type === lang.scheduleComponent.label &&
          !props.isButtonHidden &&
          !isViewOnly && (
            <ScheduleComponentButton
              disabled={props.disabled}
              variant="outlined"
              startIcon={<CalendarTodayIcon />}
              onClick={props.onHandleClick}>
              {lang.scheduleComponent.label}
            </ScheduleComponentButton>
          )}

        {props.type === lang.assemblyPublications && (
          <>
            {returnToPublicationList()}
            {isViewOnly && <ViewOnlyAlert />}
          </>
        )}
        {props.type !== lang.reviewProducts &&
          props.type !== lang.productCategory &&
          props.type !== lang.calendarScheduler &&
          props.type !== lang.scheduleComponent.label &&
          props.type !== lang.preview &&
          props.type !== lang.listLocationsPageOO &&
          props.type !== lang.selectLayout &&
          props.type !== lang.assemblyPublications &&
          props.type !== lang.productPublications &&
          props.type !== 'listLayoutsTemp' &&
          props.type !== lang.newProductCategory &&
          (isViewOnly ? (
            <>
              {props.type === lang.listLocationsPage &&
                navigateToPublicationList()}
              {props.type === lang.publications &&
                navigateToAssemblyPublication()}
              {props.type === lang.publications &&
                navigateToProductPublicationList()}
              <ViewOnlyAlert />
            </>
          ) : (
            <>
              {props.type === lang.listLocationsPage &&
                navigateToPublicationList()}
              {props.type !== lang.publications &&
                (isLayoutMissing ? (
                  <CustomTooltip
                    width={500}
                    placement="bottom"
                    title={lang.createLayoutBeforeAssembly}
                    skipValidateOverflow={true}>
                    <Button
                      variant="outlined"
                      color={
                        props.type === lang.editProductsCategory
                          ? 'error'
                          : 'primary'
                      }
                      disabled={props.disabled || !props.hasLayouts}
                      startIcon={
                        props.type === lang.editProductsCategory ? (
                          <DeleteOutlinedIcon />
                        ) : (
                          <AddIcon />
                        )
                      }
                      onClick={
                        props.type !== lang.products
                          ? props.onHandleClick
                          : onToggleCreateMenu
                      }>
                      {props.buttonLabel ? props.buttonLabel : lang.new}
                    </Button>
                  </CustomTooltip>
                ) : (
                  props.type !== lang.listLocationsPage && (
                    <Button
                      variant="outlined"
                      color={
                        props.type === lang.editProductsCategory
                          ? 'error'
                          : 'primary'
                      }
                      disabled={props.disabled}
                      startIcon={
                        props.type === lang.editProductsCategory ? (
                          <DeleteOutlinedIcon />
                        ) : (
                          <AddIcon />
                        )
                      }
                      onClick={
                        props.type !== lang.products
                          ? props.onHandleClick
                          : onToggleCreateMenu
                      }>
                      {props.buttonLabel ? props.buttonLabel : lang.new}
                    </Button>
                  )
                ))}
              {props.type === lang.publications && !isOwnerOperator && (
                <>
                  {navigateToAssemblyPublication()}
                  {navigateToProductPublicationList()}
                </>
              )}
            </>
          ))}
        {props.type === lang.newProductCategory &&
          (isViewOnly ? (
            <ViewOnlyAlert />
          ) : (
            <Button
              variant="outlined"
              startIcon={<GridViewIcon />}
              onClick={props.onNewProductCategoryClick}>
              {lang.newProductCategory}
            </Button>
          ))}
        {props.type === lang.listLocationsPageOO &&
          (isViewOnly ? (
            <ViewOnlyAlert />
          ) : (
            <Button
              variant="contained"
              onClick={() => publishModal.onOpenModal()}>
              {lang.publishAllChanges}
            </Button>
          ))}
        {props.type === lang.listLocationsPage && !isViewOnly && (
          <>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={props.onHandleClick}>
              {lang.newLocationGroup}
            </Button>
          </>
        )}
        {props.type === 'listLayoutsTemp' && isViewOnly && <ViewOnlyAlert />}
        {publishModal.open && (
          <PublicationModal
            title={lang.publicationTitle}
            subTitle={lang.publicationSubTitle}
            confirmText={lang.publish}
            cancelText={lang.cancel}
            locationCodes={props.locationCodes}
            {...(props.type === lang.listLocationsPageOO && {
              publicationType: PublicationTypes.OVERRIDES,
            })}
            open={publishModal.open}
            onClose={publishModal.onCloseModal}
            publishMessage={lang.successfullyPublished}
          />
        )}
      </Wrapper>
    </>
  );
};
